import { useEffect, useMemo, useRef, useState } from 'react';

import { usePaymentContext } from 'features/payment';
import { IPaymentMethod } from 'features/payment/types';
import { isAllowedQuickPayMethod, isCreditCardNotExpired } from 'features/payment/utils';

import { useLoyaltyUpdateIdAndPayMutation, useLoyaltyUserQuery } from 'generated/graphql-gateway';
import { useOrderPayment } from 'pages/cart/payment/order-payment/use-order-payment';
import { useAuthContext } from 'state/auth';
import { useLoyaltyUserId } from 'state/loyalty/hooks/use-loyalty-user';
import { useInRestaurantRedemptionContext } from 'state/loyalty/in-restaurant-redemption';
import logger from 'utils/logger';

// Validating expiration date for the current credit card
const isCurrentPaymentMethodValid = (
  availablePaymentMethods: IPaymentMethod[],
  selectedPaymentMethod?: string | null
) => {
  const currentPayment = availablePaymentMethods.find(
    item => item.accountIdentifier === selectedPaymentMethod
  );

  return !!currentPayment && isCreditCardNotExpired(currentPayment?.credit);
};

export const useQuickPayState = () => {
  const {
    paymentMethods,
    showAddPaymentMethod: isAddNewCardMethodSelected,
    setShowAddPaymentMethod,
    isAddGiftCardMethodSelected,
    setIsAddGiftCardMethodSelected,
  } = useOrderPayment();

  const availablePaymentMethods = useMemo(
    () => paymentMethods.filter(paymentMethod => isAllowedQuickPayMethod(paymentMethod)),
    [paymentMethods]
  );

  const {
    isOrbital,
    setIsQuickPayMethodSelected,
    isAddPaymentMethodSuccess,
    setIsAddPaymentMethodSuccess,
  } = usePaymentContext();

  // Ref to check if the component is initialized, we are going to use it to
  // allow the user to toggle the quick pay toggle button
  const isInitialized = useRef(false);
  const [selectedMethod, setSelectedMethod] = useState<string | undefined>();
  const { isQuickPayEnabled, setIsQuickPayEnabled } = useInRestaurantRedemptionContext();

  const { loyaltyUserId: loyaltyId } = useLoyaltyUserId();
  const { user } = useAuthContext();

  const { data, loading } = useLoyaltyUserQuery({
    skip: !loyaltyId,
    variables: { loyaltyId },
    fetchPolicy: 'network-only',
    // TO DO: Make an action on error...
  });

  const idPay = data?.loyaltyUserV2?.idPay;
  const isQuickPayAvailable = availablePaymentMethods.length > 0 && !!idPay;
  const [loyaltyQuickPayMutation] = useLoyaltyUpdateIdAndPayMutation();

  useEffect(() => {
    if (isInitialized.current || !idPay) {
      return;
    }
    const isSelectedMethodAvailable =
      availablePaymentMethods.findIndex(el => el.fdAccountId === idPay.storedPaymentMethodId) > -1;

    if (idPay.enabled && isSelectedMethodAvailable) {
      setIsQuickPayEnabled(true);
    }
    if (isAddPaymentMethodSuccess) {
      setSelectedMethod(
        availablePaymentMethods[availablePaymentMethods.length - 1]?.accountIdentifier || undefined
      );
      setIsQuickPayEnabled(true);
      setIsAddPaymentMethodSuccess(false);
      // The user had no payment method selected
    } else if (idPay.storedPaymentMethodId === undefined || idPay.storedPaymentMethodId === null) {
      setSelectedMethod(
        availablePaymentMethods[availablePaymentMethods.length - 1]?.accountIdentifier || undefined
      );
      // The user had a payment method selected and it's not available anymore
    } else if (!!idPay.storedPaymentMethodId && !isSelectedMethodAvailable) {
      setSelectedMethod(
        availablePaymentMethods[availablePaymentMethods.length - 1]?.accountIdentifier || undefined
      );
      setIsQuickPayEnabled(false);
    } else {
      setSelectedMethod(idPay.storedPaymentMethodId);
    }
    isInitialized.current = true;
  }, [idPay, availablePaymentMethods, isAddPaymentMethodSuccess, setIsAddPaymentMethodSuccess]);

  useEffect(() => {
    if (!idPay) {
      return;
    }
    if (
      (idPay.storedPaymentMethodId !== selectedMethod || idPay.enabled !== isQuickPayEnabled) &&
      isCurrentPaymentMethodValid(availablePaymentMethods, selectedMethod)
    ) {
      loyaltyQuickPayMutation({
        variables: {
          idAndPayInput: { storedPaymentMethodId: selectedMethod, enabled: isQuickPayEnabled },
          loyaltyId,
        },
        refetchQueries: ['LoyaltyUser'],
      });
    }
    if (!isQuickPayAvailable && idPay.storedPaymentMethodId) {
      loyaltyQuickPayMutation({
        variables: {
          idAndPayInput: { storedPaymentMethodId: undefined, enabled: false },
          loyaltyId,
        },
        refetchQueries: ['LoyaltyUser'],
      });
    }
  }, [
    idPay,
    selectedMethod,
    availablePaymentMethods,
    loyaltyQuickPayMutation,
    loyaltyId,
    isQuickPayEnabled,
    isQuickPayAvailable,
  ]);

  const handleToggle = () => {
    if (isCurrentPaymentMethodValid(availablePaymentMethods, selectedMethod)) {
      logger.info({
        message: `[INFO] Quick Pay Toggle`,
        loyaltyId: user?.loyaltyId,
        toggleStatus: !isQuickPayEnabled,
      });
    }
    setIsQuickPayEnabled(isEnabled => !isEnabled);
  };

  const handleSelection = (fdAccountId: string | undefined) => {
    setSelectedMethod(fdAccountId);
  };

  return {
    isQuickPayEnabled,
    selectedMethod,
    availablePaymentMethods,
    isAddNewCardMethodSelected,
    isAddGiftCardMethodSelected,
    isOrbital,
    isQuickPayAvailable,
    data,
    loading,
    paymentMethods,
    setShowAddPaymentMethod,
    setIsAddGiftCardMethodSelected,
    setIsAddPaymentMethodSuccess,
    loyaltyQuickPayMutation,
    setIsQuickPayMethodSelected,
    handleToggle,
    handleSelection,
  };
};
